import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSlider = () => {
  const [sliderData, setSliderData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSliderData();
  }, []);

  const fetchSliderData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/slider`);
      const data = await response.json();
      // Only use enabled slides
      const enabledSlides = (data.slides || []).filter(slide => slide.enabled);
      setSliderData(enabledSlides);
    } catch (error) {
      console.error('Error fetching slider:', error);
      // Fallback to empty array
      setSliderData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sliderData.length === 0) return;

    const timer = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(timer);
  }, [currentSlide, sliderData.length]);

  const nextSlide = () => {
    if (isTransitioning || sliderData.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % sliderData.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const prevSlide = () => {
    if (isTransitioning || sliderData.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + sliderData.length) % sliderData.length);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  if (loading) {
    return (
      <div className="relative w-full bg-gray-100 h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (sliderData.length === 0) {
    return (
      <div className="relative w-full bg-gradient-to-r from-red-600 to-red-700 h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Cep Premium</h2>
          <p className="text-xl opacity-90">Premium Teknoloji Ürünleri</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-gray-100 overflow-hidden">
      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        {sliderData.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-500 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {/* Background image */}
            <div className="absolute inset-0">
              <img
                src={slide.image}
                alt={slide.title}
                className="hidden md:block w-full h-full object-cover"
              />
              <img
                src={slide.mobileImage}
                alt={slide.title}
                className="md:hidden w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative h-full container mx-auto px-4">
              <div className="h-full flex items-center">
                <div className="max-w-2xl text-white">
                  <p className="text-sm md:text-base mb-2 opacity-90">
                    {slide.productName}
                  </p>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-base md:text-lg mb-6 opacity-90 leading-relaxed">
                    {slide.subtitle}
                  </p>
                  <Link
                    to={slide.link}
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Keşfet
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-all duration-300 shadow-lg hover:scale-110"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-all duration-300 shadow-lg hover:scale-110"
        >
          <ChevronRight size={24} />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {sliderData.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentSlide(index);
                  setTimeout(() => setIsTransitioning(false), 500);
                }
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-red-600'
                  : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
